import { useRef, useState } from 'react';
import { Botao } from '../ds/Botao.js';
import { CampoLongo } from '../ds/CampoLongo.js';
import { Folha } from '../ds/Folha.js';
import { compartilharTexto } from './compartilhar.js';

interface Props {
  texto: string;
  aoFechar: () => void;
  compartilhar?: ((dados: { text: string }) => Promise<void>) | null;
  copiar?: ((texto: string) => Promise<void>) | null;
}
export function CompartilharRelatorio({
  texto,
  aoFechar,
  compartilhar,
  copiar,
}: Props) {
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState<'compartilhar' | 'copiar' | null>(
    null,
  );
  const emCurso = useRef(false);
  const foco = useRef<HTMLDivElement>(null);
  const nativo =
    compartilhar === undefined
      ? navigator.share?.bind(navigator)
      : compartilhar;
  const prancheta =
    copiar === undefined
      ? navigator.clipboard?.writeText.bind(navigator.clipboard)
      : copiar;
  async function enviar() {
    if (emCurso.current) return;
    emCurso.current = true;
    setOcupado('compartilhar');
    setErro(null);
    setAviso(null);
    try {
      // Nenhuma leitura assíncrona antes de share: o Safari exige que a
      // chamada ainda pertença ao gesto que abriu a folha nativa.
      const resultado = await compartilharTexto(texto, nativo ?? undefined);
      if (resultado === 'indisponivel')
        setAviso('Compartilhamento indisponível. Copie o texto abaixo.');
      if (resultado === 'compartilhado')
        setAviso(
          'Compartilhamento concluído. A marcação de envio é uma ação separada.',
        );
    } catch {
      setErro('Não deu para compartilhar. Tente novamente.');
    } finally {
      emCurso.current = false;
      setOcupado(null);
    }
  }
  async function copiarTexto() {
    if (emCurso.current) return;
    emCurso.current = true;
    setOcupado('copiar');
    setErro(null);
    setAviso(null);
    try {
      if (!prancheta) throw new Error();
      await prancheta(texto);
      setAviso('Texto copiado.');
    } catch {
      setErro('Não deu para copiar. Selecione o texto e copie manualmente.');
    } finally {
      emCurso.current = false;
      setOcupado(null);
    }
  }
  return (
    <Folha
      aberta
      titulo="Compartilhar relatório"
      focoInicial={foco}
      aoFechar={() => (emCurso.current ? false : aoFechar())}
    >
      <div ref={foco} tabIndex={-1} className="relatorio__formulario">
        <CampoLongo
          rotulo="Texto do relatório"
          value={texto}
          readOnly
          rows={10}
        />
        <p>
          Confira o texto antes de compartilhar. Isso não marca o relatório como
          enviado.
        </p>
        {erro ? <p role="alert">{erro}</p> : null}
        <p role="status">{aviso}</p>
        <Botao
          disabled={ocupado === 'copiar'}
          carregando={ocupado === 'compartilhar'}
          onClick={() => {
            void enviar();
          }}
        >
          Compartilhar
        </Botao>
        <Botao
          variante="secundario"
          disabled={ocupado === 'compartilhar'}
          carregando={ocupado === 'copiar'}
          onClick={() => {
            void copiarTexto();
          }}
        >
          Copiar texto
        </Botao>
      </div>
    </Folha>
  );
}
